import React, { useState } from "react";
import { format, addHours, addDays } from "date-fns";

export default function SetTrialEndModal({
  initialDate, // Date object (trial actual)
  onSave,      // function(unixTimestamp)
  onCancel,
  disabled,
  error,
  reason,
  onReasonChange
}) {
  // Estado base
  // Estado base
  let safeDate;
  try {
    if (initialDate) {
      const d = new Date(initialDate);
      if (!isNaN(d)) {
        safeDate = d;
      } else {
        throw new Error('Fecha inicial inválida');
      }
    } else {
      throw new Error('Fecha inicial vacía');
    }
  } catch (e) {
    safeDate = new Date();
    safeDate.setHours(23, 59, 0, 0);
  }
  const [date, setDate] = useState(safeDate);
  const [dateInput, setDateInput] = useState(() => format(safeDate, 'yyyy-MM-dd'));
  const [initError, setInitError] = useState(() => {
    if (!initialDate) return '';
    const d = new Date(initialDate);
    if (isNaN(d)) return 'La fecha inicial es inválida, se usará la fecha actual.';
    return '';
  });
  const [dateError, setDateError] = useState('');
  const [customTime, setCustomTime] = useState(false);
  const [hours, setHoursState] = useState(date.getHours());
  const [minutes, setMinutesState] = useState(date.getMinutes());

  // Cuando cambia la fecha, si no hay hora personalizada, hora a 23:59
  React.useEffect(() => {
    if (!customTime) {
      setHoursState(23);
      setMinutesState(59);
    }
  }, [date, customTime]);

  const quickActions = [
    { label: "+1h", fn: () => addHours(date, 1) },
    { label: "+3h", fn: () => addHours(date, 3) },
    { label: "+6h", fn: () => addHours(date, 6) },
    { label: "+12h", fn: () => addHours(date, 12) },
    { label: "+1d", fn: () => addDays(date, 1) },
    { label: "+7d", fn: () => addDays(date, 7) },
    { label: "Fin ahora", fn: () => new Date() },
  ];

  const handleDateInputChange = (e) => {
    const value = e.target.value;
    setDateInput(value);
    if (!value) {
      setDateError('Fecha inválida');
      return;
    }
    const [year, month, day] = value.split('-').map(Number);
    if (!year || !month || !day) {
      setDateError('Fecha inválida');
      return;
    }
    const parsed = new Date(year, month - 1, day, hours, minutes, 0, 0);
    if (Number.isNaN(parsed.getTime())) {
      setDateError('Fecha inválida');
      return;
    }
    setDate(parsed);
    setDateError('');
  };

  const handleTimeChange = (e) => {
    const [h, m] = e.target.value.split(":").map(Number);
    setHoursState(h);
    setMinutesState(m);
    // Actualizar la hora en el objeto date
    const newDate = new Date(date);
    newDate.setHours(h);
    newDate.setMinutes(m);
    setDate(newDate);
  };

  const handleQuickAction = (fn) => {
    const newDate = fn();
    setDate(newDate);
    setHoursState(newDate.getHours());
    setMinutesState(newDate.getMinutes());
  };

  const handleSave = () => {
    let finalDate = new Date(date);
    if (customTime) {
      finalDate.setHours(hours);
      finalDate.setMinutes(minutes);
      finalDate.setSeconds(0);
    } else {
      finalDate.setHours(23);
      finalDate.setMinutes(59);
      finalDate.setSeconds(59);
    }
    onSave(Math.floor(finalDate.getTime() / 1000));
  };

  // El input ahora es libre, no se usa dateInputValue
  const timeInputValue = `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
  const previewDate = new Date(date);
  if (!customTime) {
    previewDate.setHours(23, 59, 59, 0);
  }

  return (
    <div>
      <div className="ui-section-title">Editar fin de trial (Stripe)</div>
      <div className="ui-section-subtitle">Elige fecha y hora exactas para el fin de la prueba.</div>
      {initError && <div style={{ color: '#c00', fontSize: 14, marginBottom: 8 }}>{initError}</div>}
      <div className="ui-field">
        <div className="ui-label">Fecha de finalización</div>
        <input
          type="date"
          value={dateInput}
          onChange={handleDateInputChange}
          className="ui-input"
          disabled={disabled}
        />
      </div>
      {dateError && <div style={{ color: '#c00', fontSize: 13, marginBottom: 6 }}>{dateError}</div>}
      <div className="ui-field">
        <label style={{ fontSize: 14, cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={customTime}
            onChange={e => setCustomTime(e.target.checked)}
            style={{ marginRight: 8 }}
            disabled={disabled}
          />
          Hora personalizada
        </label>
      </div>
      {customTime && (
        <div className="ui-field">
          <div className="ui-label">Hora de finalización</div>
          <input
            type="time"
            value={timeInputValue}
            onChange={handleTimeChange}
            className="ui-input"
            disabled={disabled}
          />
        </div>
      )}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
        {quickActions.map(({ label, fn }) => (
          <button
            key={label}
            type="button"
            onClick={() => handleQuickAction(fn)}
            className="ui-button secondary"
            disabled={disabled}
          >
            {label}
          </button>
        ))}
      </div>
      <div className="ui-field">
        <div className="ui-label">Nuevo fin de prueba</div>
        <div className="ui-input" style={{ display: 'flex', alignItems: 'center' }}>
          {format(previewDate, 'dd/MM/yyyy HH:mm')}
        </div>
      </div>
      <div className="ui-field">
        <div className="ui-label">Motivo del cambio</div>
        <textarea
          value={reason}
          onChange={onReasonChange}
          className="ui-textarea"
          placeholder="Motivo interno (requerido)"
          disabled={disabled}
        />
      </div>
      {error && <div style={{ color: '#c00', marginBottom: 12 }}>{error}</div>}
      <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
        <button
          type="button"
          onClick={onCancel}
          className="ui-button ghost"
          disabled={disabled}
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={handleSave}
          className="ui-button primary"
          disabled={disabled}
        >
          Guardar
        </button>
      </div>
    </div>
  );
}
